import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, Globe, GraduationCap } from "lucide-react";
import { Wordmark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { LANGS } from "@/lib/i18n";

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center gap-0.5 lg:gap-1 shrink-0" data-testid="lang-switch">
      <Globe className="hidden lg:block w-4 h-4 text-stone-600 me-1" />
      {LANGS.map((l) => (
        <button key={l.id} onClick={() => setLang(l.id)} data-testid={`nav-lang-${l.id}`}
          className={`px-1 max-[359px]:px-0.5 sm:px-2 md:px-1 lg:px-2.5 py-1 rounded-full text-[11px] max-[359px]:text-[10px] sm:text-xs font-semibold transition-colors ${lang === l.id ? "bg-[#1C1917] text-[#FAF7F2]" : "text-stone-700 hover:bg-[#F2ECE1]"}`}>
          {l.label}
        </button>
      ))}
    </div>
  );
};

const pill = "shrink-0 inline-flex items-center gap-1 sm:gap-1.5 lg:gap-2 px-2 max-[359px]:px-1.5 sm:px-3 lg:px-4 py-1.5 sm:py-2 text-xs max-[359px]:text-[11px] lg:text-sm font-semibold rounded-full transition-colors";

const Actions = () => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/"); };
  return (
    <div className="flex flex-wrap items-center gap-1 sm:gap-2 lg:gap-3">
      {user?.role === "teacher" && (
        <Link to="/teacher" data-testid="nav-teacher-btn" className={`${pill} text-[#047857] hover:bg-[#F2ECE1]`}><GraduationCap className="w-4 h-4" /> {t.nav.teacher}</Link>
      )}
      {user?.role === "admin" && (
        <Link to="/admin" data-testid="nav-admin-btn" className={`${pill} text-[#8B3A2B] hover:bg-[#F2ECE1]`}><ShieldCheck className="w-4 h-4" /> {t.nav.admin}</Link>
      )}
      {user ? (
        <button onClick={doLogout} data-testid="nav-logout-btn" className={`${pill} border border-[#1C1917] hover:bg-[#1C1917] hover:text-[#FAF7F2]`}><LogOut className="w-4 h-4" /> {t.nav.logout}</button>
      ) : (
        <>
          <Link to="/login" data-testid="nav-login-btn" className={`${pill} border-2 border-[#047857] text-[#047857] hover:bg-[#047857] hover:text-white`}>{t.nav.login}</Link>
          <Link to="/register" data-testid="nav-register-btn" className={`${pill} border-2 border-[#B45309] bg-[#B45309] text-white hover:bg-[#8B3A2B] hover:border-[#8B3A2B]`}><span className="xl:hidden">{t.nav.signup}</span><span className="hidden xl:inline">{t.nav.register}</span></Link>
        </>
      )}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const links = [
    { to: "/", label: t.nav.home, id: "home" },
    { to: "/library", label: t.nav.library, id: "library" },
    { to: "/samples", label: t.nav.samples, id: "samples" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-[#FAF7F2] border-b border-[#E5DCCB]" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-12 py-2 min-h-16 lg:min-h-20 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 md:gap-x-2 lg:gap-x-6 whitespace-nowrap"
        data-testid="nav-scroll-row" data-lenis-prevent>
        <Link to="/" data-testid="nav-logo" className="shrink-0"><Wordmark /></Link>
        <nav className="flex items-center gap-3 max-[359px]:gap-2 sm:gap-4 md:gap-3 lg:gap-5 xl:gap-7 shrink-0">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end data-testid={`nav-link-${l.id}`} className={({ isActive }) => `text-xs max-[359px]:text-[11px] sm:text-sm md:text-[13px] lg:text-sm font-semibold transition-colors ${isActive ? "text-[#8B3A2B]" : "text-stone-700 hover:text-[#1C1917]"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="w-full md:w-auto flex flex-wrap items-center justify-between gap-1.5 md:gap-2 lg:gap-4 shrink-0 border-t border-[#E5DCCB] pt-2 md:border-0 md:pt-0"><LangSwitch /><Actions /></div>
      </div>
    </header>
  );
};
